import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    CircularProgress,
    Typography,
    Chip,
    Tooltip
} from '@mui/material';

const EditSeoDialog = ({ open, onClose, setting, onSave, placeholders, pageLabel }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        meta_title: '',
        meta_description: '',
        keywords: '',
        og_image: ''
    });

    useEffect(() => {
        if (setting) {
            setFormData({
                meta_title: setting.meta_title || '',
                meta_description: setting.meta_description || '',
                keywords: setting.keywords || '',
                og_image: setting.og_image || ''
            });
            setError('');
            setSuccess(false);
        }
    }, [setting]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const insertPlaceholder = (field, placeholder) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field] + placeholder
        }));
    };

    const handleSubmit = async () => {
        if (!setting) return;

        setLoading(true);
        setError('');

        try {
            const payload = {
                id: setting.id,
                meta_title: formData.meta_title,
                meta_description: formData.meta_description,
                keywords: formData.keywords,
                og_image: formData.og_image
            };

            const response = await fetch('https://api.elnaf.fi/api/update_seo_setting.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const responseText = await response.text();
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (e) {
                setError('Invalid response from server.');
                setLoading(false);
                return;
            }

            if (response.ok && (data.status === 'success' || data.status === 'info')) {
                setSuccess(true);
                setTimeout(() => {
                    if (onSave) onSave();
                }, 1200);
            } else {
                setError(data.message || 'Failed to update SEO setting.');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading && !success) {
            setError('');
            setSuccess(false);
            onClose();
        }
    };

    if (!setting) return null;

    const langLabel = { 'en': 'English', 'fi': 'Finnish', 'de': 'German', 'fr': 'French' };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Edit SEO Setting
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Page: <strong>{pageLabel}</strong> — Language: <strong>{langLabel[setting.lang] || setting.lang}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        page_key: {setting.page_key} | lang: {setting.lang}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>SEO setting updated successfully!</Alert>
                    )}

                    {/* Placeholder Hints */}
                    {placeholders && placeholders.length > 0 && (
                        <Alert severity="info" sx={{ mb: 3 }} icon={false}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                📌 Available placeholders for this page:
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                {placeholders.map(ph => (
                                    <Tooltip key={ph.key} title={`${ph.desc} — Click to insert`} arrow>
                                        <Chip
                                            label={`${ph.key} — ${ph.desc}`}
                                            size="small"
                                            clickable
                                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem', bgcolor: '#e8f5e9', color: '#2e7d32' }}
                                        />
                                    </Tooltip>
                                ))}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                E.g: <code>{'Buy {{name}} from Elnaf - Best {{brand}} Products'}</code>
                            </Typography>
                        </Alert>
                    )}

                    {/* Meta Title */}
                    <TextField
                        fullWidth
                        label="Meta Title"
                        name="meta_title"
                        value={formData.meta_title}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{ mb: 3 }}
                        helperText={`${formData.meta_title.length} chars (recommended: 50-60)`}
                    />

                    {/* Placeholder düymələri - meta_title üçün */}
                    {placeholders && placeholders.length > 0 && (
                        <Box sx={{ mb: 2, mt: -2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, alignSelf: 'center' }}>
                                Insert into title:
                            </Typography>
                            {placeholders.map(ph => (
                                <Chip
                                    key={`title-${ph.key}`}
                                    label={ph.key}
                                    size="small"
                                    clickable
                                    onClick={() => insertPlaceholder('meta_title', ph.key)}
                                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer' }}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    )}

                    {/* Meta Description */}
                    <TextField
                        fullWidth
                        label="Meta Description"
                        name="meta_description"
                        value={formData.meta_description}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        variant="outlined"
                        sx={{ mb: 3 }}
                        helperText={`${formData.meta_description.length} chars (recommended: 150-160)`}
                    />

                    {/* Placeholder düymələri - meta_description üçün */}
                    {placeholders && placeholders.length > 0 && (
                        <Box sx={{ mb: 2, mt: -2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, alignSelf: 'center' }}>
                                Insert into description:
                            </Typography>
                            {placeholders.map(ph => (
                                <Chip
                                    key={`desc-${ph.key}`}
                                    label={ph.key}
                                    size="small"
                                    clickable
                                    onClick={() => insertPlaceholder('meta_description', ph.key)}
                                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer' }}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    )}

                    {/* Keywords */}
                    <TextField
                        fullWidth
                        label="Keywords"
                        name="keywords"
                        value={formData.keywords}
                        onChange={handleChange}
                        variant="outlined"
                        sx={{ mb: 3 }}
                        helperText="Comma-separated keywords"
                    />

                    {/* OG Image */}
                    <TextField
                        fullWidth
                        label="OG Image URL"
                        name="og_image"
                        value={formData.og_image}
                        onChange={handleChange}
                        variant="outlined"
                        helperText="Image URL displayed when shared on social media"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading || success}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || success}
                    startIcon={loading && <CircularProgress size={20} />}
                >
                    {success ? 'Saved!' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditSeoDialog;
